import { useCallback, useState, useRef } from 'react'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { FullView, FullViewContent } from '../../../../components/Modal'
import { ActionButton } from '../../../../components/Button'
import { Check, ChevronBackward } from '@nine-thirty-five/material-symbols-react/rounded'
import { type CreateCalendarEntryData, useCreateCalendarEntry } from '@zendia/frontend-shared'
import { axiosRequest } from '../../../../utilities/axiosClient'
import {
  CalendarEventForm,
  type CalendarEventFormRef,
} from '../../../../components/Calendar/CalendarEventForm'
import { toEventStartParam, validDateParam } from '../../../../components/Calendar/calendarUtils'
import {
  parseTasksPanelTab,
  type TasksPanelTab,
} from '../../../../components/dashboard/tasksPanelTab'

export const Route = createFileRoute('/_authenticated/calendar/new/form')({
  component: RouteComponent,
  staticData: { suppressNotifications: true },
  validateSearch: (
    search: Record<string, unknown>,
  ): { startTime: string | undefined; returnDate?: string; dashboardTab?: TasksPanelTab } => {
    return {
      startTime: typeof search.startTime === 'string' ? search.startTime : undefined,
      // The calendar date to return to on save or discard
      returnDate: validDateParam(search.returnDate),
      // Set when opened from the dashboard: save or discard goes back there, to the same Tasks
      // panel group, instead of to the calendar
      dashboardTab: parseTasksPanelTab(search.dashboardTab),
    }
  },
})

function RouteComponent() {
  const search = Route.useSearch()
  const router = useRouter()
  const { mutateAsync: createCalendarEntry } = useCreateCalendarEntry(axiosRequest)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string>()
  const calendarEventFormRef = useRef<CalendarEventFormRef>(null)

  // `eventAdded` is set after a save; the dashboard uses it to confirm when the event is. The
  // calendar needs no confirmation, since the new event appears on it.
  const returnToOrigin = useCallback(
    (eventAdded?: string) => {
      if (search.dashboardTab) {
        router.navigate({
          to: '/dashboard',
          search: { tab: search.dashboardTab, eventAdded },
          replace: true,
        })
      } else {
        router.navigate({ to: '/calendar', search: { date: search.returnDate }, replace: true })
      }
    },
    [router, search.dashboardTab, search.returnDate],
  )

  const onBack = useCallback(() => {
    if (isSubmitting) return
    returnToOrigin()
  }, [isSubmitting, returnToOrigin])

  const onSave = useCallback(
    async (data: CreateCalendarEntryData & { calendarId: string }) => {
      setIsSubmitting(true)
      setSubmitError(undefined)

      try {
        const { calendarId, ...entryData } = data
        await createCalendarEntry({ calendarId, data: entryData })
        returnToOrigin(toEventStartParam(entryData.localStart, entryData.isAllDay === true))
      } catch (error) {
        console.error(error)
        setSubmitError('Failed to create calendar event')
      } finally {
        setIsSubmitting(false)
      }
    },
    [createCalendarEntry, returnToOrigin],
  )

  return (
    <FullView
      leftAction={
        <ActionButton
          onClick={onBack}
          label="Discard"
          icon={<ChevronBackward className="size-6" />}
        />
      }
      centerContent={<div>New Event</div>}
      rightAction={
        <ActionButton
          primary
          onClick={() => {
            calendarEventFormRef.current?.submit()
          }}
          label="Save event"
          disabled={isSubmitting}
          icon={<Check className="m-auto size-6" />}
        />
      }
    >
      <FullViewContent>
        <div className="h-full">
          <CalendarEventForm
            ref={calendarEventFormRef}
            onSubmit={onSave}
            isSubmitting={isSubmitting}
            submitError={submitError}
            initialData={
              search.startTime
                ? {
                    localStart: search.startTime,
                    // Default to 1 hour duration (in minutes)
                    duration: 60,
                  }
                : undefined
            }
          />
        </div>
      </FullViewContent>
    </FullView>
  )
}
